import { ColumnDef } from '@tanstack/react-table';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { DataTableColumnHeader } from '@/components/core';
import { formatDate } from '@/lib/utils/custom-date/custom-date';
import { Button } from '@/components/ui-kit/button';
import { Department } from '../../types/department.types';

interface CreateDepartmentTableColumnsProps {
  t: (key: string) => string;
  onEdit: (department: Department) => void;
  onDelete: (department: Department) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export const createDepartmentTableColumns = ({
  t,
  onEdit,
  onDelete,
  canUpdate = true,
  canDelete = true,
}: CreateDepartmentTableColumnsProps): ColumnDef<Department>[] => [
  {
    id: 'Title',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT_NAME')} />,
    accessorFn: (row) => row.Title ?? '',
    cell: ({ row }) => <span className="truncate font-medium">{row.original.Title}</span>,
  },
  {
    id: 'Key',
    // Card: full width and wrapped, as keys are long and have no spaces to wrap at.
    meta: { filter: { type: 'text' }, mobile: 'full' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT_KEY')} />,
    accessorFn: (row) => row.Key ?? '',
    cell: ({ row }) => (
      <span className="truncate max-md:whitespace-normal max-md:break-all">
        {row.original.Key}
      </span>
    ),
  },
  {
    id: 'Description',
    // Card: full width and wrapped.
    meta: { filter: { type: 'text' }, mobile: 'full' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESCRIPTION')} />,
    accessorFn: (row) => row.Description ?? '',
    cell: ({ row }) => (
      <span className="truncate max-md:whitespace-normal max-md:break-words">
        {row.original.Description || '-'}
      </span>
    ),
  },
  {
    id: 'ActiveUsers',
    // Counted per row after the page loads; not a field the server can sort on.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ACTIVE_EMPLOYEE')} />,
    accessorFn: (row) => row.ActiveUsers ?? 0,
  },
  {
    id: 'CreatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CREATION_DATE')} />,
    accessorFn: (row) => row.CreatedDate ?? '',
    cell: ({ row }) =>
      row.original.CreatedDate ? formatDate(new Date(row.original.CreatedDate), true) : '-',
  },
  {
    id: 'LastUpdatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LAST_MODIFIED')} />,
    accessorFn: (row) => row.LastUpdatedDate ?? '',
    cell: ({ row }) =>
      row.original.LastUpdatedDate ? formatDate(new Date(row.original.LastUpdatedDate), true) : '-',
  },
  ...(canUpdate || canDelete
    ? [
        {
          id: 'actions',
          // Card: top right, as the old portal's card header.
          meta: { mobile: 'badge' as const },
          header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }: { row: { original: Department } }) => (
            <div className="flex items-center gap-1">
              {canUpdate && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(row.original)}
                  aria-label={t('EDIT')}
                >
                  <PencilSimple className="size-4" />
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(row.original)}
                  aria-label={t('DELETE')}
                >
                  <Trash className="size-4" />
                </Button>
              )}
            </div>
          ),
        },
      ]
    : []),
];
